<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAppointmentBooking } from 'dashboard/composables/useAppointmentBooking';
import AppointmentsAPI from 'dashboard/api/appointments';

const props = defineProps({
  conversationId: {
    type: [String, Number],
    default: null,
  },
  show: {
    type: Boolean,
    default: false,
  },
  prefillDateTime: {
    type: String,
    default: null,
  },
  prefillProfessionalId: {
    type: [String, Number],
    default: null,
  },
  // Audit 2026-05-07 — UX fix: card pipeline com conversa vinculada já
  // sabe o contato; passar `prefill-contact-id` evita exigir digitação
  // redundante quando AppointmentsBlock abre o modal sem conversationId.
  prefillContactId: {
    type: [String, Number],
    default: null,
  },
  // When set, the created appointment is linked to this PipelineCard
  // instead of triggering the default-pipeline auto-create path. Required
  // so agendamentos feitos pelo card aparecem dentro daquele mesmo card.
  pipelineCardId: {
    type: [String, Number],
    default: null,
  },
});

const emit = defineEmits(['close', 'created']);

const { t, locale } = useI18n();
const store = useStore();

const {
  contact,
  professionalId,
  serviceId,
  datetime,
  notes,
  partnerId,
  professionals,
  services,
  partners,
  loading,
  errors,
  fetchOptions,
  submit,
  reset,
} = useAppointmentBooking({
  conversationId: props.conversationId,
  pipelineCardId: props.pipelineCardId,
});

// Local errors for the agenda-mode dispatch path (which bypasses the
// composable). Without this, failures from store.dispatch in handleSubmit
// were swallowed silently — user clicked the button and nothing happened.
const localErrors = ref(null);
const submitting = ref(false);

// Translate backend raw error messages into actionable, localized hints.
// Falls back to the original message when no mapping matches.
function translateError(field, message) {
  const msg = String(message || '').toLowerCase();
  if (msg.includes('time slot conflicts')) {
    return t('APPOINTMENTS.BOOKING_MODAL.ERRORS.SLOT_TAKEN');
  }
  if (msg.includes('professional does not offer')) {
    return t('APPOINTMENTS.BOOKING_MODAL.ERRORS.PROFESSIONAL_NO_SERVICE');
  }
  if (
    msg.includes('not available at this time') ||
    msg.includes('out of hours')
  ) {
    return t('APPOINTMENTS.BOOKING_MODAL.ERRORS.OUT_OF_HOURS');
  }
  return String(message);
}

const displayErrors = computed(() => {
  const raw = errors.value || localErrors.value;
  if (!raw || typeof raw !== 'object') return null;
  return Object.entries(raw).reduce((acc, [field, messages]) => {
    const list = Array.isArray(messages) ? messages : [messages];
    acc[field] = list.map(m => translateError(field, m));
    return acc;
  }, {});
});

// ─── Slot-aware availability filter ──────────────────────────────────────────
// When the modal opens with a pre-filled datetime (e.g. agenda click) we
// shrink the professional list to those who CAN take that slot. The filter
// re-runs whenever datetime or service changes. Cancelled / no_show
// appointments do NOT count as conflicts (handled server-side).
const availableProfessionalIds = ref(null); // null = no filter active yet
const availableCount = ref(null);
const totalProfessionalsCount = ref(null);
const availabilityLoading = ref(false);

const hasDatetime = computed(() => Boolean(datetime.value));

const filteredProfessionals = computed(() => {
  if (!availableProfessionalIds.value) return professionals.value;
  const ids = new Set(availableProfessionalIds.value.map(Number));
  return professionals.value.filter(p => ids.has(Number(p.id)));
});

const noProfessionalsAvailable = computed(
  () =>
    hasDatetime.value &&
    availableProfessionalIds.value !== null &&
    filteredProfessionals.value.length === 0
);

async function refreshAvailableProfessionals() {
  if (!datetime.value) {
    availableProfessionalIds.value = null;
    availableCount.value = null;
    totalProfessionalsCount.value = null;
    return;
  }
  availabilityLoading.value = true;
  try {
    const params = { scheduled_at: datetime.value };
    if (serviceId.value) params.service_id = serviceId.value;
    const response = await AppointmentsAPI.availableProfessionals(params);
    const data = response.data?.data || {};
    availableProfessionalIds.value = (data.professionals || []).map(p => p.id);
    availableCount.value =
      data.available_count ?? availableProfessionalIds.value.length;
    totalProfessionalsCount.value = data.total_professionals ?? null;
    // If currently selected professional was filtered out, clear it.
    if (
      professionalId.value &&
      !availableProfessionalIds.value.includes(Number(professionalId.value))
    ) {
      professionalId.value = null;
    }
  } catch {
    // On error, fall back to unfiltered list — better than blocking the user.
    availableProfessionalIds.value = null;
    availableCount.value = null;
    totalProfessionalsCount.value = null;
  } finally {
    availabilityLoading.value = false;
  }
}

watch(datetime, () => {
  refreshAvailableProfessionals();
});
watch(serviceId, () => {
  // Re-filter when service changes (duration may differ)
  if (datetime.value) refreshAvailableProfessionals();
});

// Warn when the selected professional has no services linked (m:n table
// professional_services empty). The composable falls back to showing all
// services in that case, but the backend rejects with 422
// "professional does not offer this service".
const selectedProfessional = computed(() =>
  professionals.value.find(p => p.id === Number(professionalId.value))
);
const professionalHasNoServices = computed(() => {
  if (!selectedProfessional.value) return false;
  const ids = selectedProfessional.value.service_ids;
  return Array.isArray(ids) && ids.length === 0;
});

// Contact picker (used when no conversationId)
const contactQuery = ref('');
const contactResults = ref([]);
const selectedContact = ref(null);
const searchLoading = ref(false);

let searchTimer = null;

// M6: clean up pending debounce timer on unmount to prevent memory leaks
onUnmounted(() => clearTimeout(searchTimer));

async function searchContacts() {
  const q = contactQuery.value.trim();
  if (q.length < 2) {
    contactResults.value = [];
    return;
  }
  searchLoading.value = true;
  clearTimeout(searchTimer);
  searchTimer = setTimeout(async () => {
    try {
      await store.dispatch('contacts/search', { search: q, page: 1 });
      contactResults.value = store.getters['contacts/getContacts'].slice(0, 8);
    } catch {
      contactResults.value = [];
    } finally {
      searchLoading.value = false;
    }
  }, 300);
}

function selectContact(c) {
  selectedContact.value = c;
  contactQuery.value = c.name;
  contactResults.value = [];
}

// Unified canSubmit that handles both conversation-based and picker modes
const effectiveCanSubmit = computed(() => {
  const hasContact = props.conversationId
    ? !!(contact.value && contact.value.id)
    : !!(selectedContact.value && selectedContact.value.id);
  return (
    hasContact &&
    !!professionalId.value &&
    !!serviceId.value &&
    !!datetime.value &&
    !loading.value &&
    !submitting.value
  );
});

async function applyPrefill() {
  if (props.prefillDateTime) {
    datetime.value = props.prefillDateTime;
  }
  if (props.prefillProfessionalId) {
    professionalId.value = props.prefillProfessionalId;
  }
  // No-conversation mode: pre-populate the contact picker so the user
  // doesn't have to retype a contact the parent already knows.
  if (!props.conversationId && props.prefillContactId) {
    try {
      const c = await store.dispatch('contacts/show', {
        id: props.prefillContactId,
      });
      const resolved =
        c || store.getters['contacts/getContact'](props.prefillContactId);
      if (resolved && resolved.id) {
        selectedContact.value = resolved;
        contactQuery.value = resolved.name || '';
        contactResults.value = [];
      }
    } catch {
      // Silently ignore — user can still pick contact manually
    }
  }
}

// H6: Focus management
const dialogRef = ref(null);
let triggerEl;
onUnmounted(() => triggerEl?.focus?.());

onMounted(() => {
  triggerEl = document.activeElement;
  if (props.show) {
    fetchOptions();
    applyPrefill();
    // Focus first focusable element in modal
    setTimeout(() => {
      const focusable = dialogRef.value?.querySelector(
        'input,select,button,textarea,[tabindex]:not([tabindex="-1"])'
      );
      focusable?.focus();
    }, 50);
  }
});

watch(
  () => props.show,
  newVal => {
    if (newVal) {
      reset();
      selectedContact.value = null;
      contactQuery.value = '';
      contactResults.value = [];
      fetchOptions();
      applyPrefill();
    }
  }
);

async function handleSubmit() {
  localErrors.value = null;
  submitting.value = true;
  try {
    // In no-conversation mode, inject the selected contact into the payload
    if (!props.conversationId && selectedContact.value) {
      const payload = {
        contact_id: selectedContact.value.id,
        professional_id: professionalId.value,
        service_id: serviceId.value,
        scheduled_at: datetime.value,
        notes: notes.value || undefined,
      };
      if (partnerId.value) payload.partner_id = partnerId.value;
      if (props.pipelineCardId) payload.pipeline_card_id = props.pipelineCardId;
      const appt = await store.dispatch('appointmentsModule/create', payload);
      emit('created', appt);
      emit('close');
    } else {
      const appt = await submit();
      emit('created', appt);
      emit('close');
    }
  } catch (err) {
    // Surface the error to the user. Without this, agenda-mode failures
    // (validation errors, license blocks, missing professional-service
    // link) were swallowed and the button click looked like a no-op.
    // eslint-disable-next-line no-console
    console.error('[AppointmentBookingModal] submit failed:', err);
    const responseErrors = err?.response?.data?.errors;
    const responseError = err?.response?.data?.error;
    if (responseErrors && typeof responseErrors === 'object') {
      localErrors.value = responseErrors;
    } else if (responseError) {
      localErrors.value = { base: [String(responseError)] };
    } else {
      localErrors.value = {
        base: [
          String(err?.message || t('APPOINTMENTS.BOOKING_MODAL.GENERIC_ERROR')),
        ],
      };
    }
  } finally {
    submitting.value = false;
  }
}

function formatPrice(cents, currency = 'BRL') {
  if (cents == null) return '';
  const localeStr = (locale.value || 'pt_BR').replace('_', '-');
  return new Intl.NumberFormat(localeStr, {
    style: 'currency',
    currency,
  }).format(cents / 100);
}

function professionalLabel(p) {
  return p.specialty ? `${p.name} — ${p.specialty}` : p.name;
}

function serviceLabel(s) {
  const base = `${s.name} — ${s.duration_minutes} min`;
  if (s.default_price_cents) {
    return `${base} · ${formatPrice(s.default_price_cents, s.currency)}`;
  }
  return base;
}
</script>

<template>
  <Transition
    enter-active-class="transition duration-300 ease-out"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="transition duration-200 ease-in"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div
      v-if="show"
      class="fixed inset-0 z-50 flex items-center justify-center bg-n-slate-12/40 backdrop-blur-md p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="booking-modal-title"
    >
      <div
        ref="dialogRef"
        class="bg-n-background rounded-[1.25rem] w-full max-w-md shadow-[0_24px_50px_-12px_rgba(0,0,0,0.25)] ring-1 ring-n-strong flex flex-col max-h-full overflow-hidden transition-transform duration-300 transform scale-100"
      >
        <!-- Header com ícone estético -->
        <div
          class="flex items-center justify-between px-6 py-5 border-b border-n-weak bg-n-alpha-1/50"
        >
          <div class="flex items-center gap-3">
            <div
              class="size-10 rounded-xl bg-gradient-to-br from-n-brand/20 to-n-brand/5 flex items-center justify-center flex-shrink-0 ring-1 ring-n-brand/20 shadow-inner"
            >
              <span
                class="i-lucide-calendar-plus size-5 text-n-brand drop-shadow-sm"
                aria-hidden="true"
              />
            </div>
            <h2
              id="booking-modal-title"
              class="text-lg font-semibold text-n-slate-12 tracking-tight"
            >
              {{ t('APPOINTMENTS.BOOKING_MODAL.TITLE') }}
            </h2>
          </div>
          <button
            class="size-8 rounded-full text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-all inline-flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-n-brand"
            data-testid="close-btn"
            :aria-label="t('APPOINTMENTS.BOOKING_MODAL.CANCEL')"
            @click="emit('close')"
          >
            <span class="i-lucide-x size-4.5" aria-hidden="true" />
          </button>
        </div>

        <div
          class="flex-1 overflow-y-auto px-6 py-6 space-y-5 custom-scrollbar"
        >
          <!-- Contact section: conversation-based -->
          <template v-if="conversationId">
            <div
              v-if="contact && contact.id"
              class="rounded-xl border border-n-teal-6/30 bg-gradient-to-r from-n-teal-2 to-n-teal-1 p-3.5 flex items-center gap-3 shadow-sm"
            >
              <div
                class="size-10 rounded-full bg-n-teal-3 text-n-teal-11 flex items-center justify-center font-bold text-sm flex-shrink-0 shadow-inner ring-1 ring-n-teal-4"
              >
                {{ (contact.name || '?').charAt(0).toUpperCase() }}
              </div>
              <div class="flex-1 min-w-0">
                <p
                  class="text-[11px] uppercase text-n-teal-10 font-bold tracking-wider mb-0.5"
                >
                  {{ t('APPOINTMENTS.BOOKING_MODAL.CLIENT_FOUND') }}
                </p>
                <p
                  class="text-sm text-n-slate-12 font-semibold truncate"
                  data-testid="contact-display"
                >
                  {{ contact.name }}
                </p>
                <p
                  v-if="contact.phoneNumber ?? contact.phone_number"
                  class="text-xs text-n-slate-11 truncate mt-0.5"
                >
                  {{ contact.phoneNumber ?? contact.phone_number }}
                </p>
              </div>
            </div>
            <div
              v-else
              class="rounded-xl border border-n-amber-6/30 bg-gradient-to-r from-n-amber-2 to-n-amber-1 p-3.5 flex items-center gap-2 text-sm text-n-amber-11 shadow-sm"
            >
              <span
                class="i-lucide-alert-circle size-4.5 flex-shrink-0"
                aria-hidden="true"
              />
              <span class="font-medium">{{
                t('APPOINTMENTS.BOOKING_MODAL.NO_CONTACT')
              }}</span>
            </div>
          </template>

          <!-- Contact section: picker (agenda mode) -->
          <template v-else>
            <div>
              <label
                class="block text-xs font-semibold text-n-slate-11 uppercase tracking-wide mb-1.5"
              >
                {{ t('APPOINTMENTS.BOOKING_MODAL.SEARCH_CONTACT') }}
              </label>
              <div class="relative">
                <span
                  v-if="searchLoading"
                  class="i-lucide-loader-2 absolute left-3.5 top-1/2 -translate-y-1/2 size-4 text-n-brand animate-spin"
                  aria-hidden="true"
                />
                <span
                  v-else
                  class="i-lucide-search absolute left-3.5 top-1/2 -translate-y-1/2 size-4 text-n-slate-10 pointer-events-none"
                  aria-hidden="true"
                />

                <input
                  v-model="contactQuery"
                  type="text"
                  data-testid="contact-search"
                  class="!mb-0 w-full rounded-xl border border-n-strong bg-n-background hover:bg-n-alpha-0 focus:bg-n-background !pl-10 !pr-3 py-2.5 text-sm text-n-slate-12 transition-all shadow-sm focus:outline-none focus:border-n-brand focus:ring-2 focus:ring-n-brand/20 placeholder-n-slate-9"
                  :placeholder="t('APPOINTMENTS.BOOKING_MODAL.SEARCH_CONTACT')"
                  @input="searchContacts"
                />

                <Transition
                  enter-active-class="transition duration-200 ease-out"
                  enter-from-class="opacity-0 -translate-y-2"
                  enter-to-class="opacity-100 translate-y-0"
                  leave-active-class="transition duration-150 ease-in"
                  leave-from-class="opacity-100 translate-y-0"
                  leave-to-class="opacity-0 -translate-y-2"
                >
                  <div
                    v-if="contactResults.length && contactQuery.length >= 2"
                    class="absolute z-20 mt-1.5 w-full border border-n-strong rounded-xl bg-n-background shadow-xl max-h-56 overflow-y-auto ring-1 ring-black/5"
                  >
                    <button
                      v-for="c in contactResults"
                      :key="c.id"
                      type="button"
                      class="w-full text-left px-3 py-2.5 hover:bg-n-alpha-1 text-sm text-n-slate-12 border-b border-n-weak last:border-b-0 flex items-center gap-3 transition-colors"
                      @click="selectContact(c)"
                    >
                      <div
                        class="size-8 rounded-full bg-gradient-to-br from-n-teal-3 to-n-teal-4 text-n-teal-11 flex items-center justify-center font-bold text-xs flex-shrink-0 shadow-inner"
                      >
                        {{ (c.name || '?').charAt(0).toUpperCase() }}
                      </div>
                      <div class="flex-1 min-w-0">
                        <p class="font-semibold text-n-slate-12 truncate">
                          {{ c.name }}
                        </p>
                        <p
                          v-if="c.phone_number"
                          class="text-[11px] text-n-slate-10 truncate mt-0.5 font-medium tracking-wide"
                        >
                          {{ c.phone_number }}
                        </p>
                      </div>
                    </button>
                  </div>
                </Transition>
              </div>

              <Transition
                enter-active-class="transition duration-300 ease-out"
                enter-from-class="opacity-0 scale-95"
                enter-to-class="opacity-100 scale-100"
              >
                <div
                  v-if="selectedContact"
                  class="mt-3 rounded-xl border border-n-teal-6/30 bg-gradient-to-r from-n-teal-2 to-n-teal-1 p-3 flex items-center gap-3 shadow-sm"
                >
                  <div
                    class="size-10 rounded-full bg-n-teal-3 text-n-teal-11 flex items-center justify-center font-bold flex-shrink-0 ring-1 ring-n-teal-4 shadow-inner"
                  >
                    {{ (selectedContact.name || '?').charAt(0).toUpperCase() }}
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-semibold text-n-slate-12 truncate">
                      {{ selectedContact.name }}
                    </p>
                    <p
                      v-if="selectedContact.phone_number"
                      class="text-xs text-n-slate-11 truncate mt-0.5"
                    >
                      {{ selectedContact.phone_number }}
                    </p>
                  </div>
                  <span
                    class="i-lucide-check-circle-2 text-n-teal-10 size-5 flex-shrink-0"
                    aria-hidden="true"
                  />
                </div>
              </Transition>
            </div>
          </template>

          <div class="h-px bg-n-weak w-full my-2" />

          <!-- Professional select -->
          <div>
            <label
              class="flex items-center justify-between text-xs font-semibold text-n-slate-11 uppercase tracking-wide mb-1.5"
            >
              <span>{{
                t('APPOINTMENTS.BOOKING_MODAL.PROFESSIONAL_LABEL')
              }}</span>
              <span
                v-if="
                  hasDatetime && availableCount !== null && !availabilityLoading
                "
                class="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                :class="
                  availableCount === 0
                    ? 'bg-n-amber-3 text-n-amber-11'
                    : 'bg-n-teal-3 text-n-teal-11'
                "
                data-testid="availability-counter"
              >
                {{
                  t('APPOINTMENTS.BOOKING_MODAL.AVAILABILITY_COUNT', {
                    available: availableCount,
                    total: totalProfessionalsCount ?? availableCount,
                  })
                }}
              </span>
              <span
                v-else-if="availabilityLoading"
                class="i-lucide-loader-2 size-3 animate-spin text-n-slate-10"
                aria-hidden="true"
              />
            </label>
            <div class="relative group">
              <span
                class="i-lucide-user absolute left-3.5 top-1/2 -translate-y-1/2 size-4 text-n-slate-10 group-focus-within:text-n-brand transition-colors pointer-events-none"
                aria-hidden="true"
              />
              <select
                v-model="professionalId"
                :disabled="noProfessionalsAvailable"
                data-testid="professional-select"
                class="!mb-0 w-full appearance-none !bg-none rounded-xl border border-n-strong bg-n-background hover:bg-n-alpha-0 focus:bg-n-background pl-10 pr-10 py-2.5 text-sm text-n-slate-12 transition-all shadow-sm focus:outline-none focus:border-n-brand focus:ring-2 focus:ring-n-brand/20 disabled:opacity-60 disabled:bg-n-alpha-1 disabled:cursor-not-allowed cursor-pointer"
              >
                <option :value="null" disabled>
                  {{ t('APPOINTMENTS.BOOKING_MODAL.SELECT_PROFESSIONAL') }}
                </option>
                <option
                  v-for="p in filteredProfessionals"
                  :key="p.id"
                  :value="p.id"
                >
                  {{ professionalLabel(p) }}
                </option>
              </select>
              <span
                class="i-lucide-chevron-down absolute right-3.5 top-1/2 -translate-y-1/2 size-4 text-n-slate-10 pointer-events-none"
                aria-hidden="true"
              />
            </div>
            <Transition
              enter-active-class="transition duration-200 ease-out"
              enter-from-class="opacity-0 -translate-y-1"
              enter-to-class="opacity-100 translate-y-0"
            >
              <p
                v-if="noProfessionalsAvailable"
                class="mt-2 text-[11px] font-medium text-n-amber-11 flex items-center gap-1 bg-n-amber-2 px-2 py-1.5 rounded-lg border border-n-amber-5"
                data-testid="no-professionals-available"
              >
                <span
                  class="i-lucide-alert-triangle size-3"
                  aria-hidden="true"
                />
                {{ t('APPOINTMENTS.BOOKING_MODAL.NO_PROFESSIONALS_AT_SLOT') }}
              </p>
            </Transition>
          </div>

          <!-- Service select -->
          <div>
            <label
              class="block text-xs font-semibold text-n-slate-11 uppercase tracking-wide mb-1.5 flex items-center justify-between"
            >
              {{ t('APPOINTMENTS.BOOKING_MODAL.SERVICE_LABEL') }}
            </label>
            <div class="relative group">
              <span
                class="i-lucide-clipboard-list absolute left-3.5 top-1/2 -translate-y-1/2 size-4 text-n-slate-10 group-focus-within:text-n-brand transition-colors pointer-events-none"
                :class="{ 'opacity-50': !professionalId }"
                aria-hidden="true"
              />
              <select
                v-model="serviceId"
                :disabled="!professionalId"
                data-testid="service-select"
                class="!mb-0 w-full appearance-none !bg-none rounded-xl border border-n-strong bg-n-background hover:bg-n-alpha-0 focus:bg-n-background pl-10 pr-10 py-2.5 text-sm text-n-slate-12 transition-all shadow-sm focus:outline-none focus:border-n-brand focus:ring-2 focus:ring-n-brand/20 disabled:opacity-60 disabled:bg-n-alpha-1 disabled:cursor-not-allowed cursor-pointer"
              >
                <option :value="null" disabled>
                  {{ t('APPOINTMENTS.BOOKING_MODAL.SELECT_SERVICE') }}
                </option>
                <option v-for="s in services" :key="s.id" :value="s.id">
                  {{ serviceLabel(s) }}
                </option>
              </select>
              <span
                class="i-lucide-chevron-down absolute right-3.5 top-1/2 -translate-y-1/2 size-4 text-n-slate-10 pointer-events-none"
                :class="{ 'opacity-50': !professionalId }"
                aria-hidden="true"
              />
            </div>
            <Transition
              enter-active-class="transition duration-200 ease-out"
              enter-from-class="opacity-0 -translate-y-1"
              enter-to-class="opacity-100 translate-y-0"
            >
              <p
                v-if="professionalHasNoServices"
                class="mt-2 text-[11px] font-medium text-n-amber-11 flex items-center gap-1 bg-n-amber-2 px-2 py-1.5 rounded-lg border border-n-amber-5"
                data-testid="no-services-warning"
              >
                <span
                  class="i-lucide-alert-triangle size-3"
                  aria-hidden="true"
                />
                {{ t('APPOINTMENTS.BOOKING_MODAL.PROFESSIONAL_NO_SERVICES') }}
              </p>
            </Transition>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <!-- Datetime -->
            <div>
              <label
                class="block text-xs font-semibold text-n-slate-11 uppercase tracking-wide mb-1.5"
              >
                {{ t('APPOINTMENTS.BOOKING_MODAL.DATETIME_LABEL') }}
              </label>
              <div class="relative group">
                <input
                  v-model="datetime"
                  type="datetime-local"
                  data-testid="datetime-input"
                  class="w-full rounded-xl border border-n-strong bg-n-background hover:bg-n-alpha-0 focus:bg-n-background px-3.5 py-2.5 text-sm text-n-slate-12 transition-all shadow-sm focus:outline-none focus:border-n-brand focus:ring-2 focus:ring-n-brand/20 cursor-pointer"
                />
              </div>
            </div>

            <!-- Partner (optional) -->
            <div v-if="partners.length">
              <label
                class="block text-xs font-semibold text-n-slate-11 uppercase tracking-wide mb-1.5"
              >
                {{ t('APPOINTMENTS.BOOKING_MODAL.PARTNER_LABEL') }}
              </label>
              <div class="relative group">
                <span
                  class="i-lucide-handshake absolute left-3.5 top-1/2 -translate-y-1/2 size-4 text-n-slate-10 group-focus-within:text-n-brand transition-colors pointer-events-none"
                  aria-hidden="true"
                />
                <select
                  v-model="partnerId"
                  data-testid="partner-select"
                  class="!mb-0 w-full appearance-none !bg-none rounded-xl border border-n-strong bg-n-background hover:bg-n-alpha-0 focus:bg-n-background pl-10 pr-10 py-2.5 text-sm text-n-slate-12 transition-all shadow-sm focus:outline-none focus:border-n-brand focus:ring-2 focus:ring-n-brand/20 cursor-pointer"
                >
                  <option :value="null">
                    {{ t('APPOINTMENTS.BOOKING_MODAL.PARTNER_NONE') }}
                  </option>
                  <option v-for="p in partners" :key="p.id" :value="p.id">
                    {{ p.name }}
                  </option>
                </select>
                <span
                  class="i-lucide-chevron-down absolute right-3.5 top-1/2 -translate-y-1/2 size-4 text-n-slate-10 pointer-events-none"
                  aria-hidden="true"
                />
              </div>
            </div>
          </div>

          <!-- Notes -->
          <div>
            <label
              class="block text-xs font-semibold text-n-slate-11 uppercase tracking-wide mb-1.5"
            >
              {{ t('APPOINTMENTS.BOOKING_MODAL.NOTES_LABEL') }}
            </label>
            <textarea
              v-model="notes"
              rows="3"
              data-testid="notes-input"
              class="w-full rounded-xl border border-n-strong bg-n-background hover:bg-n-alpha-0 focus:bg-n-background px-3.5 py-3 text-sm text-n-slate-12 resize-none transition-all shadow-sm focus:outline-none focus:border-n-brand focus:ring-2 focus:ring-n-brand/20 placeholder-n-slate-9 leading-relaxed"
              :placeholder="t('APPOINTMENTS.BOOKING_MODAL.NOTES_PLACEHOLDER')"
            />
          </div>

          <!-- Errors -->
          <Transition
            enter-active-class="transition duration-300 ease-out"
            enter-from-class="opacity-0 scale-95 translate-y-2"
            enter-to-class="opacity-100 scale-100 translate-y-0"
          >
            <div
              v-if="displayErrors"
              class="rounded-xl border border-n-ruby-6 bg-gradient-to-r from-n-ruby-2 to-n-ruby-1 p-3 shadow-sm text-xs text-n-ruby-11 flex items-start gap-2"
              data-testid="errors"
            >
              <span
                class="i-lucide-alert-octagon size-4 flex-shrink-0 mt-0.5"
                aria-hidden="true"
              />
              <div class="space-y-1 flex-1">
                <p v-for="(messages, field) in displayErrors" :key="field">
                  <strong class="capitalize">{{ `${field}:` }}</strong>
                  <span class="ml-1">{{
                    Array.isArray(messages) ? messages.join(', ') : messages
                  }}</span>
                </p>
              </div>
            </div>
          </Transition>
        </div>

        <!-- Footer com Actions -->
        <div
          class="flex items-center justify-end gap-3 px-6 py-5 border-t border-n-weak bg-n-alpha-1/30"
        >
          <button
            class="px-5 py-2.5 text-sm font-semibold rounded-xl text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-all focus:outline-none focus:ring-2 focus:ring-n-slate-6"
            data-testid="cancel-btn"
            @click="emit('close')"
          >
            {{ t('APPOINTMENTS.BOOKING_MODAL.CANCEL') }}
          </button>
          <button
            class="inline-flex items-center justify-center gap-2 px-6 py-2.5 text-sm font-semibold rounded-xl bg-n-brand text-white hover:bg-n-brand/90 hover:shadow-lg hover:shadow-n-brand/20 hover:-translate-y-px active:translate-y-0 active:shadow-sm disabled:opacity-50 disabled:hover:shadow-none disabled:hover:-translate-y-0 disabled:cursor-not-allowed transition-all"
            :disabled="!effectiveCanSubmit"
            data-testid="create-appointment-btn"
            @click="handleSubmit"
          >
            <span
              v-if="loading || submitting"
              class="i-lucide-loader-2 size-4.5 animate-spin"
              aria-hidden="true"
            />
            <span
              v-else
              class="i-lucide-calendar-check-2 size-4.5"
              aria-hidden="true"
            />
            <span v-if="loading || submitting">{{
              t('APPOINTMENTS.BOOKING_MODAL.LOADING')
            }}</span>
            <span v-else>{{ t('APPOINTMENTS.BOOKING_MODAL.SUBMIT') }}</span>
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>
